import { useRef } from 'react'

import { VideoControlsAction, VideoFeedEvent } from '@/features/video-player'

import { mixpanel } from '../mixpanel'
import {
  AnalyticsScreen,
  MixpanelEvents,
  PlayerEvents,
} from '../types/playerEvents'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type EventPayload = any

export const useMixpanel = (screenName?: AnalyticsScreen) => {
  const streamStartTimeRef = useRef<number | null>(null)
  const streamReceivedTimeRef = useRef<number | null>(null)

  const markStreamRequested = () => {
    streamStartTimeRef.current = performance.now()
    streamReceivedTimeRef.current = null
  }

  const createFullEventName = (eventName: string): string => {
    const name = screenName ? `${screenName}:` : ''
    return `${name}${eventName}`
  }

  const trackEvent = (eventName: MixpanelEvents, data?: EventPayload) => {
    const name = createFullEventName(eventName)
    mixpanel.track(name, data)
  }

  const createIncidentVideoEventHandler = (data?: EventPayload) => {
    return (event: VideoFeedEvent) => {
      switch (event) {
        case VideoFeedEvent.BeginRequesting:
          trackEvent(PlayerEvents.requestIncidentClip, data)
          return
        case VideoFeedEvent.Received:
          trackEvent(PlayerEvents.receivedIncidentClip, data)
          return
        case VideoFeedEvent.Playing:
          trackEvent(PlayerEvents.playIncidentClip, data)
          return
        default:
          return
      }
    }
  }

  const createCameraEventHandler = (data?: EventPayload) => {
    return (event: VideoFeedEvent) => {
      switch (event) {
        case VideoFeedEvent.BeginRequesting:
          streamStartTimeRef.current = performance.now()
          streamReceivedTimeRef.current = null
          trackEvent(PlayerEvents.requestLiveFeed, data)
          return
        case VideoFeedEvent.Received:
          streamReceivedTimeRef.current = performance.now()
          trackEvent(PlayerEvents.receivedLiveFeed, data)
          return
        case VideoFeedEvent.Playing: {
          trackEvent(PlayerEvents.playLiveFeed, data)
          const startTime = streamStartTimeRef.current
          const receivedTime = streamReceivedTimeRef.current
          if (startTime != null) {
            const now = performance.now()
            mixpanel.track(PlayerEvents.liveStreamLatency, {
              ...data,
              screen_name: screenName,
              total_ms: Math.round(now - startTime),
              request_ms: receivedTime
                ? Math.round(receivedTime - startTime)
                : null,
              buffer_ms: receivedTime ? Math.round(now - receivedTime) : null,
            })
            streamStartTimeRef.current = null
            streamReceivedTimeRef.current = null
          }
          return
        }
        default:
          return
      }
    }
  }

  const createPlayerActionHandler = (data?: EventPayload) => {
    return (action: VideoControlsAction) => {
      switch (action) {
        case VideoControlsAction.Backward10:
          trackEvent(PlayerEvents.backward10, data)
          return
        case VideoControlsAction.Forward10:
          trackEvent(PlayerEvents.forward10, data)
          return
        case VideoControlsAction.Play:
          trackEvent(PlayerEvents.play, data)
          return
        case VideoControlsAction.Pause:
          trackEvent(PlayerEvents.pause, data)
          return
        case VideoControlsAction.Fullscreen:
          trackEvent(PlayerEvents.fullscreen, data)
          return
        default:
          return
      }
    }
  }

  return {
    trackEvent,
    markStreamRequested,
    createPlayerActionHandler,
    createCameraEventHandler,
    createIncidentVideoEventHandler,
  }
}
